import * as React from 'react'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
} from '@/components/ui/card'

export interface TimePickerProps {
  time?: string // Format: "HH:mm"
  onTimeChange: (time: string | undefined) => void
  disabled?: boolean
  className?: string
}

export function TimePicker({
  time,
  onTimeChange,
  disabled,
  className,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [hours, setHours] = React.useState('09')
  const [minutes, setMinutes] = React.useState('00')

  React.useEffect(() => {
    if (time) {
      const [h, m] = time.split(':')
      setHours(h || '09')
      setMinutes(m || '00')
    }
  }, [time])

  const handleConfirm = () => {
    const timeString = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
    onTimeChange(timeString)
    setIsOpen(false)
  }

  const displayTime = time || '09:00'

  // Quick picks (e.g. "now + 5 min") can produce any minute value, so make sure
  // the current one is always selectable alongside the standard quarter steps.
  const minuteOptions = React.useMemo(() => {
    const steps = [0, 15, 30, 45].map((m) => String(m).padStart(2, '0'))
    return steps.includes(minutes)
      ? steps
      : [...steps, minutes].sort((a, b) => Number(a) - Number(b))
  }, [minutes])

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full justify-start text-left font-normal"
      >
        <Clock className="mr-2 h-4 w-4" />
        {displayTime}
      </Button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <Card className="absolute z-50 mt-2 w-64 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="flex flex-col items-center">
                  <label className="text-xs text-muted-foreground mb-1">
                    Hours
                  </label>
                  <select
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="border rounded px-3 py-2 text-lg font-semibold"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={String(i).padStart(2, '0')}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="text-2xl font-bold mt-6">:</span>
                <div className="flex flex-col items-center">
                  <label className="text-xs text-muted-foreground mb-1">
                    Minutes
                  </label>
                  <select
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    className="border rounded px-3 py-2 text-lg font-semibold"
                  >
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button className="flex-1" onClick={handleConfirm}>
                  Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
